// The install guide in Japanese: the same sections, anchors, links and
// file names as the English one in install-guide.ts, which reads it through
// `installGuide("ja")`. The names a device prints on its own screens are the
// ones its Japanese system shows (設定, USB接続, HOMEメニュー); the names of
// homebrew tools are their own (VitaShell, Homebrew Launcher, Cydia).

import type { GuideSection, Paragraph } from "./install-guide.ts";

export const GUIDE_PARTS_JA = { needs: "必要なもの", file: "ファイル", steps: "インストール" } as const;

export const GUIDE_INTRO_JA: readonly Paragraph[] = [
  [
    "Pocket Studio のパッケージは自作ソフト（ホームブリュー）で、デバイスのメーカーが出したプログラムではありません。",
    "ゲーム機ではカスタムファームウェアかホームブリューローダーが、iPod touch では脱獄が必要です。",
    "Android スマートフォンでは設定の変更が 1 つ必要です。",
  ],
  [
    "各セクションでは、デバイスをホームブリューが動く状態にするコミュニティのガイドを紹介し、そのうえでダウンロードしたファイルの置き場所とゲームの起動方法を説明します。",
    "パッケージはデバイスがインストールするファイルそのもの、またはそれと一緒に置くファイルをまとめた ",
    { code: ".zip" },
    " です。",
    { code: ".zip" },
    " は先にコンピューターで展開してください。",
  ],
];

export const INSTALL_GUIDE_JA: readonly GuideSection[] = [
  {
    device: "psp",
    anchor: "psp",
    title: "PSP",
    summary: ".zip、カスタムファームウェア",
    needs: [
      [
        "カスタムファームウェアが動く PSP。ARK-4 はファームウェア 6.60 か 6.61 のすべての PSP で動きます。モデルごとのインストール方法は ",
        { link: "ARK-4 の Wiki", href: "https://github.com/PSP-Archive/ARK-4/wiki" },
        " にあります。",
      ],
    ],
    file: [
      [
        "中に ",
        { code: "PSP/GAME/<name>" },
        " フォルダーがある ",
        { code: ".zip" },
        "。フォルダーには ",
        { code: "EBOOT.PBP" },
        " と、ゲームによってはデータファイルが入っています。組み込みのゲームのフォルダー名は ",
        { code: "Studio<Name>" },
        " で、ほかのゲームはそれぞれ自分の名前を付けています。",
      ],
      [{ code: "EBOOT.PBP" }, " だけで配られるゲームもあります。"],
    ],
    steps: [
      [
        "PSP を USB ケーブルでコンピューターにつないで「設定」の「USB接続」を選ぶか、メモリースティックをカードリーダーに挿します。",
      ],
      [
        "ファイルを展開し、zip の中の ",
        { code: "PSP/GAME" },
        " にあるゲームのフォルダーを、メモリースティックの ",
        { code: "PSP/GAME" },
        " にコピーします。メモリースティックにそのフォルダーがなければ作ってください。",
      ],
      [
        { code: "EBOOT.PBP" },
        " だけの場合は、",
        { code: "PSP/GAME" },
        " にゲーム用のフォルダーを作り、その中にファイルを入れます。",
      ],
      ["PSP で「ゲーム」から「メモリースティック」を開き、ゲームを起動します。"],
    ],
    notes: [],
  },
  {
    device: "vita",
    anchor: "vita",
    title: "PS Vita",
    summary: ".vpk、HENkaku",
    needs: [
      [
        "HENkaku が動き、VitaShell が入った PS Vita。",
        { link: "vita.hacks.guide", href: "https://vita.hacks.guide/" },
        " の手順で、molecularShell（別名の VitaShell）とともに HENkaku を入れ、そのあと Enso を入れられます。",
      ],
    ],
    file: [[{ code: ".vpk" }, "。VitaShell がインストールするパッケージ形式です。"]],
    steps: [
      [
        "VitaShell で SELECT を押すと、コンピューターとの接続が始まります。USB ではメモリーカードがドライブとして見え、FTP では Wi-Fi 経由でファイルを送れます。SELECT ボタンを USB と FTP のどちらに使うかは、START で開く設定で選びます。",
      ],
      [{ code: ".vpk" }, " を Vita の ", { code: "ux0:data/" }, " にコピーします。"],
      ["VitaShell でそのファイルを選んで × を押し、もう一度 × を押してインストールします。"],
      ["インストールが終わると、LiveArea にゲームのバブルが現れます。タップするとゲームが始まります。"],
    ],
    notes: [
      [
        "組み込みのゲームのパッケージは安全なホームブリューで、拡張された権限を求めないため、HENkaku の「安全でないホームブリュー」の設定はオフのままで動きます。",
        "安全でないホームブリューのゲームでは、インストール中に VitaShell が拡張された権限について確認し、その設定をオンにしたときだけ動きます。",
      ],
    ],
  },
  {
    device: "3ds",
    anchor: "3ds",
    title: "ニンテンドー3DS",
    summary: ".3dsx、Luma3DS",
    needs: [
      [
        "カスタムファームウェアの Luma3DS と Homebrew Launcher が入ったニンテンドー3DS か 2DS。",
        { link: "3ds.hacks.guide", href: "https://3ds.hacks.guide/" },
        " の手順で Luma3DS を入れられ、最後のページ「Finalizing Setup」で HOMEメニューに Homebrew Launcher が加わります。",
      ],
    ],
    file: [
      [
        { code: ".3dsx" },
        "。Homebrew Launcher が起動するプログラムです。CIA ではなく、HOMEメニューには何もインストールしません。",
      ],
      [
        "SD カードにデータファイルを置くゲームは、",
        { code: ".3dsx" },
        " とそのファイルをまとめた ",
        { code: ".zip" },
        " で配られます。",
      ],
    ],
    steps: [
      ["3DS の電源を切り、SD カードをコンピューターに挿します。"],
      [
        { code: ".3dsx" },
        " を SD カードのいちばん上の ",
        { code: "3ds" },
        " フォルダー（",
        { code: "sdmc:/3ds/" },
        "）にコピーします。",
        { code: "Nintendo 3DS" },
        " フォルダーではありません。",
        { code: ".zip" },
        " の場合は、中身をフォルダー構成のまま SD カードのいちばん上にコピーします。",
      ],
      ["カードを戻して 3DS の電源を入れ、HOMEメニューから Homebrew Launcher を開きます。"],
      ["Homebrew Launcher に ", { code: "sdmc:/3ds/" }, " のプログラムが並びます。ゲームを選ぶと始まります。"],
    ],
    notes: [],
  },
  {
    device: "ipod-touch",
    anchor: "ipod-touch",
    title: "iPod touch",
    summary: ".ipa、iOS 6.1、脱獄済み",
    needs: [
      [
        "iOS 6.1.x で脱獄し、Cydia が入った iPod touch（第 4 世代）。",
        { link: "ios.cfw.guide", href: "https://ios.cfw.guide/get-started/iPod-touch-(4th-generation).html" },
        " に、この iPod の iOS のバージョンごとの脱獄方法があります。使うのは非紐付きの Aquila です。",
      ],
      [
        "iOS 6 の Cydia が今のリポジトリのほとんどにつながるには、ルート証明書の更新が必要です。iPod の Safari で ",
        { link: "tlsroot.litten.ca", href: "https://tlsroot.litten.ca/" },
        " から Signed iOS Bundle を開き、インストールしてください。",
      ],
      [
        "次に Cydia から AppSync Unified をインストールします。iOS 5 以降に対応し、Apple が署名していないアプリを iOS がインストールできるようにします。追加するリポジトリは",
        { link: "その紹介ページ", href: "https://github.com/akemin-dayo/AppSync" },
        "に書かれています。インストールしたら iPod を一度再起動してください。",
      ],
    ],
    file: [
      [
        { code: ".ipa" },
        "。パッケージは iOS 6.1.x の iPod touch 第 4 世代向けに作られています。",
      ],
    ],
    steps: [
      ["iPod を USB ケーブルでコンピューターにつなぎます。"],
      [
        "Legacy iOS Kit で ",
        { code: ".ipa" },
        " をインストールします。起動して App Management を開き、Install IPA (appinst) を選んでファイルを指定します。インストール方法ごとに必要なものは、",
        { link: "IPA のインストールについてのページ", href: "https://github.com/LukeZGD/Legacy-iOS-Kit/wiki/Install-IPA-AppSync" },
        "にあります。",
      ],
      ["インストールが終わると、ホーム画面にゲームのアイコンが現れます。タップするとゲームが始まります。"],
    ],
    notes: [],
  },
  {
    device: "android",
    anchor: "android",
    title: "Android スマートフォン",
    summary: ".apk、Android 4.3 以降",
    needs: [
      [
        "Android 4.3（API 18）以降で、32 ビット ARM プロセッサー（armeabi-v7a）か 64 ビットのもの（arm64-v8a）を積んだスマートフォンかタブレット。",
        "root 化は必要ありません。",
      ],
    ],
    file: [[{ code: ".apk" }, "。"]],
    steps: [
      [
        "Android 7.1 以前では、「設定」の「セキュリティ」で「提供元不明のアプリ」をオンにします。",
        "Android 8 以降では、ファイルを開いたときにスマートフォンが確認するので、そのファイルを開くアプリからのインストールを許可してください。",
      ],
      [
        { code: ".apk" },
        " を USB ケーブルでスマートフォンにコピーするか、スマートフォンでダウンロードし、ファイルマネージャーか「ダウンロード」アプリから開きます。",
      ],
      ["「インストール」をタップし、次に「開く」をタップします。以後、ゲームはアプリの一覧にあります。"],
    ],
    notes: [],
  },
];
